import { Fragment, useCallback } from 'react';

import { MenuItem } from '../../designSystem/MenuItem';
import { Checkbox, CheckboxProps } from '../../designSystem/Checkbox';
import { cn } from '../../lib/styles';
import type { SelectOption } from './Select.types';
import { SelectOptionLabel } from './SelectOptionLabel';

type CheckboxMenuItemProps<T> = {
  option: SelectOption<T | null | undefined>;
  isChecked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  className?: string;
  menuItemClassName?: string;
  left?: React.ReactNode;
  right?: React.ReactNode;
  markdownLabels?: boolean;
  alignLabelStart?: boolean;
  onItemClick: (option: SelectOption<T | null | undefined>, checked: boolean, indeterminate?: boolean) => void;
} & Pick<CheckboxProps, 'labelClassName'>;

export function CheckboxMenuItem<T>({
  option,
  isChecked,
  indeterminate,
  disabled,
  labelClassName,
  className,
  menuItemClassName,
  left,
  right,
  markdownLabels = false,
  alignLabelStart = false,
  onItemClick,
}: CheckboxMenuItemProps<T>) {
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  return (
    <div className={cn('flex flex-col', className)}>
      <MenuItem
        key={String(option.value)}
        className={cn('w-full group [&_*]:pointer-events-none', menuItemClassName)}
        disabled={option.disabled || disabled}
        onClick={() => {
          onItemClick(option, !isChecked, indeterminate);
        }}
      >
        {left}

        <Checkbox
          checked={isChecked}
          label={<SelectOptionLabel label={option.label} markdown={markdownLabels} />}
          disabled={option.disabled || disabled}
          description={option.description}
          indeterminate={indeterminate}
          alignLabelStart={alignLabelStart}
          labelClassName={labelClassName}
          rightContent={option.rightContent}
          onClick={handleClick}
        />

        {right}
      </MenuItem>

      {option.additionalContent}
    </div>
  );
}

type CheckboxMenuItemsProps<T> = {
  options: SelectOption<T | null | undefined>[];
  values?: T[];
  className?: string;
  onValuesChange?: (
    values: T[],
    /** Whether the value was added (true) or removed (false) */
    checked: boolean,
    /** The value that was added or removed */
    value: T,
  ) => void;
  onItemClick?: (option: SelectOption<T | null | undefined>, checked: boolean) => void;
} & Pick<CheckboxMenuItemProps<T>, 'labelClassName' | 'markdownLabels' | 'alignLabelStart'>;

export function CheckboxMenuItems<T>({
  options,
  values,
  className,
  labelClassName,
  markdownLabels = false,
  alignLabelStart = false,
  onValuesChange,
  onItemClick,
}: CheckboxMenuItemsProps<T>) {
  const handleMultipleChange = (optionValue: T | null | undefined, checked: boolean) => {
    if (!values || !onValuesChange) return;

    if (checked) {
      if (optionValue !== null && optionValue !== undefined) {
        onValuesChange([...values, optionValue], checked, optionValue);
      }
    } else {
      if (optionValue !== null && optionValue !== undefined) {
        onValuesChange(
          values.filter((v) => v !== optionValue),
          checked,
          optionValue,
        );
      }
    }
  };

  const handleItemClick = (option: SelectOption<T | null | undefined>, checked: boolean) => {
    onItemClick?.(option, checked);
    handleMultipleChange(option.value, checked);
  };

  return (
    <div className={className}>
      {options.map((option, index) => {
        const isChecked = values?.includes(option.value as T) || false;
        // Options are pre-ordered by group; show a header when the group changes.
        const showGroupHeader = !!option.group && option.group !== options[index - 1]?.group;

        return (
          <Fragment key={String(option.value)}>
            {showGroupHeader && (
              <div className="px-2.5 pt-2 pb-1 font-body-sm font-semibold text-text-tertiary">{option.group}</div>
            )}

            <CheckboxMenuItem
              option={option}
              isChecked={isChecked}
              onItemClick={handleItemClick}
              labelClassName={labelClassName}
              markdownLabels={markdownLabels}
              alignLabelStart={alignLabelStart}
            />
          </Fragment>
        );
      })}
    </div>
  );
}
